import React, { useState, useEffect } from 'react';
import './Whitepapers.css';

/* ── Icons ──────────────────────────────────────────────── */
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const SpinnerIcon = () => (
  <svg className="wp-spin" width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
  </svg>
);

const ViewIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

/* ── Skeleton card ──────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="wp-card wp-card--skeleton">
    <div className="wp-card__header">
      <div className="wp-skel wp-skel--tag" />
      <div className="wp-skel wp-skel--count" />
    </div>
    <div className="wp-card__body">
      <div className="wp-skel wp-skel--title" />
      <div className="wp-skel wp-skel--line" />
      <div className="wp-skel wp-skel--line wp-skel--short" />
    </div>
    <div className="wp-card__footer">
      <div className="wp-skel wp-skel--btn" />
      <div className="wp-skel wp-skel--btn" />
    </div>
  </div>
);

function Whitepapers() {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchWhitepapers();
  }, []);

  const fetchWhitepapers = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/insights/whitepaper`);
      const result = await res.json();
      if (result.success) setInsights(result.data);
    } catch (err) {
      console.error('Error fetching whitepapers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (insight) => {
    setSelectedInsight(insight);
    setPdfLoading(true);
    setShowViewModal(true);
    
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/insights/${insight.type}/${insight.slug}`);
      const result = await res.json();
      if (result.success) {
        setSelectedInsight(result.data);
      }
    } catch (err) {
      console.error('Error fetching insight details:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadRequest = (insight) => {
    setSelectedInsight(insight);
    setShowDownloadModal(true);
    setError('');
    setSuccess(false);
    setFormData({ name: '', email: '' });
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  // FIXED: Updated download handler to properly download PDF files
  const handleDownload = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    const emailRx = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (!emailRx.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setDownloading(true);
    setError('');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/insights/${selectedInsight.type}/${selectedInsight.slug}/download`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Download failed');
      }

      // Get the file as blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedInsight.fileName || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSuccess(true);
      setTimeout(() => {
        setShowDownloadModal(false);
        setSuccess(false);
        fetchWhitepapers();
      }, 1800);
    } catch (err) {
      console.error('Download error:', err);
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const closeModals = () => {
    if (!downloading) {
      setShowViewModal(false);
      setShowDownloadModal(false);
      setError('');
      setSuccess(false);
      setSelectedInsight(null);
      setPdfLoading(false);
    }
  };

  return (
    <div className="wp-page">
      <div className="wp-hero">
        <div className="wp-hero__inner">
          <p className="wp-hero__eyebrow">BaiRees Global Advisory</p>
          <h1 className="wp-hero__title">Whitepapers</h1>
          <p className="wp-hero__sub">
            In-depth research and strategic insights on emerging technologies,
            industry trends, and transformative business models.
          </p>
        </div>
      </div>

      <div className="wp-body">
        {loading ? (
          <div className="wp-grid">
            {[1, 2, 3].map((n) => <SkeletonCard key={n} />)}
          </div>
        ) : insights.length === 0 ? (
          <div className="wp-empty">
            <p>No whitepapers available at this time.</p>
          </div>
        ) : (
          <div className="wp-grid">
            {insights.map((insight) => (
              <div key={insight._id} className="wp-card wp-card--featured">
                <div className="wp-card__header">
                  <span className="wp-tag">Whitepaper</span>
                  <span className="wp-downloads">
                    <DownloadIcon />
                    {insight.downloadCount}
                  </span>
                </div>
                <div className="wp-card__body">
                  <h3 className="wp-card__title">{insight.title}</h3>
                  <p className="wp-card__desc">{insight.excerpt}</p>
                  {insight.tags && insight.tags.length > 0 && (
                    <div className="wp-card__tags">
                      {insight.tags.map((tag, i) => (
                        <span key={i} className="wp-tag-sm">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="wp-card__footer">
                  <button
                    className="wp-btn wp-btn--view"
                    onClick={() => handleView(insight)}
                  >
                    <ViewIcon />
                    Preview Whitepaper
                  </button>
                  <button
                    className="wp-btn wp-btn--download"
                    onClick={() => handleDownloadRequest(insight)}
                  >
                    <DownloadIcon />
                    Download PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Modal with Iframe PDF Viewer */}
      {showViewModal && selectedInsight && (
        <div className="wp-overlay" onClick={closeModals}>
          <div className="wp-modal wp-modal--pdf" onClick={(e) => e.stopPropagation()}>
            <div className="wp-modal__header">
              <div>
                <p className="wp-modal__eyebrow">Whitepaper</p>
                <h2 className="wp-modal__title">{selectedInsight.title}</h2>
              </div>
              <button className="wp-modal__close" onClick={closeModals}>
                <CloseIcon />
              </button>
            </div>
            <div className="wp-modal__body wp-modal__body--pdf">
              {pdfLoading ? (
                <div className="wp-pdf-loading">
                  <div className="wp-spinner"></div>
                  <p>Loading PDF viewer...</p>
                </div>
              ) : selectedInsight.fileUrl ? (
                <iframe
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(selectedInsight.fileUrl)}&embedded=true`}
                  title={selectedInsight.title}
                  className="wp-pdf-iframe"
                  frameBorder="0"
                  allowFullScreen
                />
              ) : (
                <div className="wp-pdf-error">
                  <p>PDF viewer not available. Please download the file instead.</p>
                  <button 
                    className="wp-btn wp-btn--download"
                    onClick={() => {
                      setShowViewModal(false);
                      handleDownloadRequest(selectedInsight);
                    }}
                  >
                    <DownloadIcon />
                    Download PDF
                  </button>
                </div>
              )}
            </div>
            <div className="wp-modal__footer">
              <div className="wp-view-stats">
                <span>👁️ {selectedInsight.viewCount} views</span>
                <span>📥 {selectedInsight.downloadCount} downloads</span>
              </div>
              <button 
                className="wp-btn wp-btn--download"
                onClick={() => {
                  setShowViewModal(false);
                  handleDownloadRequest(selectedInsight);
                }}
              >
                <DownloadIcon />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Modal - Requires name and email */}
      {showDownloadModal && (
        <div className="wp-overlay" onClick={closeModals}>
          <div className="wp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wp-modal__header">
              <div>
                <p className="wp-modal__eyebrow">Download Whitepaper</p>
                <h2 className="wp-modal__title">{selectedInsight?.title}</h2>
              </div>
              <button className="wp-modal__close" onClick={closeModals} disabled={downloading}>
                <CloseIcon />
              </button>
            </div>
            {success ? (
              <div className="wp-modal__success">
                <div className="wp-modal__success-icon">✓</div>
                <p>Your download has started!</p>
                <p className="wp-modal__success-sub">Thank you, {formData.name}!</p>
              </div>
            ) : (
              <div className="wp-modal__body">
                <p className="wp-modal__intro">
                  Please provide your details to download this whitepaper.
                </p>
                {error && <div className="wp-modal__error">{error}</div>}
                <form onSubmit={handleDownload}>
                  <div className="wp-field">
                    <label className="wp-field__label">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      disabled={downloading}
                      required
                    />
                  </div>
                  <div className="wp-field">
                    <label className="wp-field__label">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="you@company.com"
                      disabled={downloading}
                      required
                    />
                  </div>
                  <button type="submit" className="wp-btn wp-btn--submit" disabled={downloading}>
                    {downloading ? <><SpinnerIcon /> Processing…</> : <><DownloadIcon /> Download Now</>}
                  </button>
                </form>
                <p className="wp-modal__privacy">
                  Your details are used only to deliver this resource and will not be shared.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Whitepapers;